import { useMemo, useCallback } from 'react';
import { useBulkPlaybookUpdateTableContext } from 'ion-experimental/app/bulk-playbook-update/BulkPlaybookUpdateTableContext';
import {
  useBulkPlaybookUpdateModalContext,
  BULK_PLAYBOOK_UPDATE_MODALS,
} from 'ion-experimental/app/bulk-playbook-update/BulkPlaybookUpdateModalContext';
import { notify, useBulkUpdatePipelineIntegrations } from '@ion/api';
import buildPipelineIntegrationUpdateRequests from './buildPipelineIntegrationUpdateRequests';

export default function useConfirmUpdateModal({ onSuccess }) {
  const { selectedIntegrationId, selectedPlaybookId, selectedPipelineIds, pipelines, clearSelection, integrations } =
    useBulkPlaybookUpdateTableContext();

  const { activeModal, onCloseModal } = useBulkPlaybookUpdateModalContext();

  const [bulkUpdate, { loading: isSubmitting, progress }] = useBulkUpdatePipelineIntegrations();

  const isOpen = activeModal === BULK_PLAYBOOK_UPDATE_MODALS.CONFIRM_UPDATE;

  // Find selected integration and playbook
  const selectedIntegration = useMemo(
    () => integrations?.find(integration => integration.id === selectedIntegrationId),
    [integrations, selectedIntegrationId]
  );

  const targetPlaybook = useMemo(
    () => selectedIntegration?.playbooks?.find(playbook => playbook.id === selectedPlaybookId),
    [selectedIntegration, selectedPlaybookId]
  );

  // Get selected pipelines
  const selectedPipelines = useMemo(
    () => pipelines.filter(pipeline => selectedPipelineIds.has(pipeline.id)),
    [pipelines, selectedPipelineIds]
  );

  // Handle form submission
  const handleSubmit = useCallback(
    async formData => {
      const updateRequests = buildPipelineIntegrationUpdateRequests({
        selectedPipelines,
        selectedIntegrationId,
        selectedPlaybookId,
        formData,
      });

      let summary;
      try {
        summary = await bulkUpdate(updateRequests);
      } catch (error) {
        // bulkUpdate captures per-batch failures and resolves with a summary, so
        // a throw here is unexpected. Surface it rather than failing silently.
        console.error('Bulk update failed:', error);
        notify.error({ message: 'Bulk update failed. Please try again or contact support.' });
        return;
      }

      const { total, succeededCount, failedCount } = summary;

      if (failedCount === 0) {
        notify.success({
          message: `${succeededCount} pipeline${succeededCount === 1 ? '' : 's'} updated successfully.`,
        });
        clearSelection();
        onCloseModal();
        if (onSuccess) {
          onSuccess();
        }
        return;
      }

      if (succeededCount === 0) {
        notify.warning({
          title: 'Bulk update failed',
          message: `None of the ${total} pipelines were updated. Please try again.`,
        });
        return;
      }

      notify.warning({
        title: 'Some pipelines were not updated',
        message: `${succeededCount} of ${total} updated, ${failedCount} failed. Re-run the bulk update to retry the rest.`,
      });
      if (onSuccess) {
        onSuccess();
      }
    },
    [selectedPipelines, selectedIntegrationId, selectedPlaybookId, bulkUpdate, clearSelection, onCloseModal, onSuccess]
  );

  return {
    isOpen,
    onClose: onCloseModal,
    selectedPipelines,
    selectedIntegration,
    targetPlaybook,
    integrationId: selectedIntegrationId,
    handleSubmit,
    isSubmitting,
    progress,
  };
}
